// src/services/auth/auth.js
import PermissionsService from "./permissions.js";
import { sessionState } from "@/state/sessionState";

class AuthService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL;
    this.token = null;
    this.currentUserData = null;
  }

  // ==========================================
  // MÉTODOS DE MANEJO DE COOKIES
  // ==========================================

  getCookie(name) {
    if (typeof document === "undefined") return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  }

  getCSRFToken() {
    const posiblesNombres = ["csrftoken", "csrf_token", "XSRF-TOKEN"];

    for (const nombre of posiblesNombres) {
      const token = this.getCookie(nombre);
      if (token) {
        return token;
      }
    }
    return null;
  }

  getJWTToken() {
    const cookieToken = this.getCookie("iiotefintel_cookie");
    if (cookieToken) {
      return cookieToken;
    }

    if (this.token) {
      return this.token;
    }
    return null;
  }

  getRefreshToken() {
    return this.getCookie("iiotefintel_refresh_cookie");
  }

  // ==========================================
  // MÉTODOS DE AUTENTICACIÓN PRINCIPALES
  // ==========================================

  async login(credentials) {
    try {
      // Limpieza preventiva
      PermissionsService.limpiarPermisos();

      await new Promise((resolve) => setTimeout(resolve, 50));

      const response = await fetch(`${this.baseURL}/api/auth/login/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": this.getCSRFToken() || "",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await this.handleErrorResponse(response);
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      const data = await response.json();

      // Validar respuesta
      if (!data.user) {
        throw new Error("Respuesta inválida del servidor");
      }

      if (data.access) {
        this.token = data.access;

        try {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 1);
          document.cookie = `iiotefintel_cookie=${data.access}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
        } catch (cookieError) {
          // Ignorar error de cookie
        }
      }

      if (data.refresh) {
        try {
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 7);
          document.cookie = `iiotefintel_refresh_cookie=${data.refresh}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
        } catch (refreshError) {
          // Ignorar error de cookie
        }
      }

      // Obtener datos completos del usuario
      try {
        const fullUserData = await this.getUserFullData(data.user.pk || data.user.id);
        data.user = fullUserData;
      } catch (userDataError) {
        // Fallback silencioso
      }

      // Integración con sistema de permisos
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await PermissionsService.cargarPermisosUsuarioActual();
      } catch (permError) {
        // Continuar aunque falle la carga de permisos
      }

      return data;
    } catch (error) {
      PermissionsService.limpiarPermisos();
      this.clearAuth();

      throw new Error(this.getFriendlyErrorMessage(error.message));
    }
  }

  async validateToken() {
    try {
      const jwtToken = this.getJWTToken();
      if (!jwtToken) {
        throw new Error("No hay token JWT disponible");
      }

      try {
        const payload = JSON.parse(atob(jwtToken.split(".")[1]));
        const expiracion = payload.exp * 1000;
        const ahora = Date.now();

        if (ahora >= expiracion) {
          throw new Error("JWT expirado");
        }

        const userData = {
          id: payload.user_id || payload.sub || payload.id,
          email: payload.email,
          username: payload.username,
          exp: payload.exp,
          iat: payload.iat,
        };

        if (!userData.id) {
          throw new Error("JWT no contiene user ID válido");
        }

        try {
          const fullUserData = await this.getUserFullData(userData.id);
          this.currentUserData = fullUserData;

          if (!PermissionsService.currentUserPermissions) {
            try {
              await PermissionsService.cargarPermisosUsuarioActual();
            } catch (permError) {
              // Continuar
            }
          }

          return fullUserData;
        } catch (fullDataError) {
          this.currentUserData = userData;

          if (!PermissionsService.currentUserPermissions) {
            try {
              await PermissionsService.cargarPermisosUsuarioActual();
            } catch (permError) {
              // Continuar
            }
          }

          return userData;
        }
      } catch (jwtError) {
        throw new Error("Token inválido");
      }
    } catch (error) {
      PermissionsService.limpiarPermisos();
      this.clearAuth();
      throw error;
    }
  }

  async getUserFullData(userId) {
    try {
      const token = this.getJWTToken();
      if (!token) {
        throw new Error("No se encontró JWT token");
      }

      const response = await fetch(`${this.baseURL}/usuarios/api/usuarios/${userId}/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-CSRFToken": this.getCSRFToken() || "",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          PermissionsService.limpiarPermisos();
          this.clearAuth();
          throw new Error("Sesión expirada");
        }

        const errorData = await this.handleErrorResponse(response);
        throw new Error(errorData.message || `Error ${response.status}`);
      }

      const userData = await response.json();
      this.currentUserData = userData;

      return userData;
    } catch (error) {
      console.error("Error al obtener datos completos del usuario:", error);
      throw error;
    }
  }

  async changePassword(passwordData) {
    try {
      if (!passwordData.oldPassword || !passwordData.newPassword) {
        throw new Error("Contraseña actual y nueva contraseña son requeridas");
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error("Las contraseñas nuevas no coinciden");
      }

      const currentUser = await this.getCurrentUser();
      if (!currentUser || !currentUser.id) {
        throw new Error("No se pudo identificar el usuario actual");
      }

      const response = await fetch(`${this.baseURL}/usuarios/api/usuarios/${currentUser.id}/cambiar_password/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": this.getCSRFToken() || "",
        },
        body: JSON.stringify({
          password_antigua: passwordData.oldPassword,
          password: passwordData.newPassword,
          password2: passwordData.confirmPassword,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch (parseError) {
          throw new Error(`Error del servidor (${response.status})`);
        }

        throw new Error(this.mapChangePasswordError(errorData));
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        data = { success: true, message: "Contraseña cambiada exitosamente" };
      }

      return {
        success: true,
        message: "Contraseña cambiada exitosamente",
        data,
      };
    } catch (error) {
      throw new Error(this.mapChangePasswordError(error));
    }
  }

  async getCurrentUser() {
    try {
      if (this.currentUserData) {
        return this.currentUserData;
      }

      const token = this.getJWTToken();
      if (!token) {
        throw new Error("No hay token JWT disponible");
      }

      const response = await fetch(`${this.baseURL}/api/auth/login/`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-CSRFToken": this.getCSRFToken() || "",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          PermissionsService.limpiarPermisos();
          this.clearAuth();
          throw new Error("Sesión expirada");
        }
        throw new Error("No se pudo obtener información del usuario actual");
      }

      const userData = await response.json();
      this.currentUserData = userData;

      return userData;
    } catch (error) {
      console.error("Error al obtener el usuario actual:", error);
      throw error;
    }
  }

  async logout() {
    try {
      const jwtToken = this.getJWTToken();

      if (jwtToken) {
        try {
          await fetch(`${this.baseURL}/api/auth/logout/`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwtToken}`,
              "X-CSRFToken": this.getCSRFToken() || "",
            },
          });
        } catch (logoutError) {
          // Ignorar error de logout en servidor
        }
      }
    } catch (error) {
      // Ignorar
    } finally {
      PermissionsService.limpiarPermisos();
      this.clearAuth();
    }
  }

  // ==========================================
  // MÉTODO PRINCIPAL PARA REQUESTS AUTENTICADAS
  // ==========================================

  async authenticatedRequest(endpoint, options = {}) {
    try {
      const url = endpoint.startsWith("http") ? endpoint : `${this.baseURL}${endpoint}`;

      const jwtToken = this.getJWTToken();
      if (!jwtToken) {
        const error = new Error("No hay sesión activa");
        error.codigo = "NO_SESSION";
        throw error;
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
        ...options.headers,
      };

      const metodosQueNecesitanCSRF = ["POST", "PUT", "PATCH", "DELETE"];
      const metodo = options.method || "GET";

      if (metodosQueNecesitanCSRF.includes(metodo.toUpperCase())) {
        const csrfToken = this.getCSRFToken();
        if (csrfToken) {
          headers["X-CSRFToken"] = csrfToken;
        }
      }

      const response = await fetch(url, {
        ...options,
        credentials: "include",
        headers,
      });

      if (response.status === 204) {
        return {
          success: true,
          status: 204,
          deleted: metodo === "DELETE",
          message: "Operación exitosa sin contenido",
        };
      }

      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");

      const tieneContenidoJSON = contentType && contentType.includes("application/json");
      const contentLengthEsCero = contentLength === "0";

      if (!response.ok) {
        if (response.status === 401) {
          sessionState.isSoftLocked = true;

          const error = new Error("Sesión expirada suavemente. Esperando reautenticación.");
          error.codigo = "SESSION_SOFT_LOCK";
          throw error;
        }

        if (response.status === 403) {
          const errorData = await this.handleErrorResponse(response);
          if (errorData.detail && errorData.detail.includes("CSRF")) {
            const error = new Error("Error de validación CSRF. Recarga la página.");
            error.codigo = "CSRF_ERROR";
            throw error;
          } else {
            const error = new Error("Sin permisos para realizar esta acción.");
            error.codigo = "FORBIDDEN";
            throw error;
          }
        }

        const errorData = await this.handleErrorResponse(response);
        const error = new Error(errorData.message || `Error ${response.status}`);
        error.codigo = `HTTP_${response.status}`;
        throw error;
      }

      if (contentLengthEsCero || !tieneContenidoJSON) {
        return {
          success: true,
          status: response.status,
          message: "Operación exitosa",
        };
      }

      try {
        const text = await response.text();

        if (!text || text.trim() === "") {
          return {
            success: true,
            status: response.status,
            message: "Operación exitosa",
          };
        }

        const data = JSON.parse(text);
        return data;
      } catch (parseError) {
        return {
          success: true,
          status: response.status,
          message: "Operación exitosa (respuesta no JSON)",
          parseError: parseError.message,
        };
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error en authenticatedRequest:", error);
      }
      throw error;
    }
  }

  // ==========================================
  // MÉTODOS DE LIMPIEZA Y VERIFICACIÓN
  // ==========================================

  clearAuth() {
    this.token = null;
    this.currentUserData = null;

    if (typeof window !== "undefined") {
      window.authToken = null;
    }

    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("auth_token");
    }

    if (typeof document !== "undefined") {
      const cookiesToClear = ["iiotefintel_cookie", "iiotefintel_refresh_cookie"];

      cookiesToClear.forEach((cookieName) => {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      });
    }
  }

  isAuthenticated() {
    const jwtToken = this.getJWTToken();

    if (!jwtToken) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(jwtToken.split(".")[1]));
      const expiracion = payload.exp * 1000;
      const ahora = Date.now();

      if (ahora >= expiracion) {
        PermissionsService.limpiarPermisos();
        this.clearAuth();
        return false;
      }

      return true;
    } catch (error) {
      PermissionsService.limpiarPermisos();
      this.clearAuth();
      return false;
    }
  }

  // ==========================================
  // MÉTODOS DE MANEJO DE ERRORES
  // ==========================================

  async handleErrorResponse(response) {
    try {
      const errorData = await response.json();
      return errorData;
    } catch (jsonError) {
      return {
        message: response.statusText || `Error ${response.status}`,
        status: response.status,
      };
    }
  }

  mapChangePasswordError(error) {
    const errorMessage = error.message || error.detail || error.non_field_errors?.[0] || "Error desconocido";

    const errorMappings = {
      "Invalid password": "La contraseña actual no es correcta",
      "Wrong password": "La contraseña actual no es correcta",
      old_password: "La contraseña actual no es correcta",
      incorrect: "La contraseña actual no es correcta",
      "Password too short": "La nueva contraseña debe tener al menos 8 caracteres",
      "too short": "La nueva contraseña debe tener al menos 8 caracteres",
      "Password too common": "La nueva contraseña es muy común. Elige una más segura",
      "too common": "La nueva contraseña es muy común. Elige una más segura",
      "Password too similar": "La nueva contraseña es muy similar a tu información personal",
      "too similar": "La nueva contraseña es muy similar a tu información personal",
      "entirely numeric": "La nueva contraseña no puede ser completamente numérica",
      numeric: "La nueva contraseña no puede ser completamente numérica",
      "Passwords do not match": "Las contraseñas nuevas no coinciden",
      "do not match": "Las contraseñas nuevas no coinciden",
      "password mismatch": "Las contraseñas nuevas no coinciden",
      "Authentication credentials were not provided": "Sesión expirada. Inicia sesión nuevamente",
      "Token has expired": "Sesión expirada. Inicia sesión nuevamente",
      "Invalid token": "Sesión inválida. Inicia sesión nuevamente",
      "Permission denied": "No tienes permisos para realizar esta acción",
      "Failed to fetch": "Error de conexión. Verifica tu internet",
      NetworkError: "Error de red. Intenta nuevamente",
      "Network request failed": "Error de red. Intenta nuevamente",
    };

    for (const [key, friendlyMessage] of Object.entries(errorMappings)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return friendlyMessage;
      }
    }

    return "Error al cambiar la contraseña. Verifica que tu contraseña actual sea correcta y que la nueva cumpla los requisitos de seguridad.";
  }

  getFriendlyErrorMessage(errorMessage) {
    const friendlyMessages = {
      "Invalid credentials": "Email o contraseña incorrectos",
      "Unable to log in with provided credentials": "Credenciales incorrectas",
      "This field is required": "Todos los campos son obligatorios",
      "Enter a valid email address": "Ingrese un email válido",
      "This password is too short": "La contraseña es muy corta",
      "User account is disabled": "Cuenta deshabilitada. Contacte al administrador",
      "Token has expired": "Sesión expirada. Inicie sesión nuevamente",
      "Invalid token": "Sesión inválida. Inicie sesión nuevamente",
      "Token inválido": "Sesión expirada. Inicie sesión nuevamente",
      "Failed to fetch": "Error de conexión. Verifique su internet",
      NetworkError: "Error de red. Intente nuevamente",
      "TypeError: Failed to fetch": "No se puede conectar al servidor",
      "Internal Server Error": "Error interno del servidor. Intente más tarde",
      "Service Unavailable": "Servicio no disponible. Intente más tarde",
      "Bad Gateway": "Error de conexión al servidor",
      "Respuesta inválida del servidor": "Error en la respuesta del servidor",
      "Error de autenticación": "Error al autenticar. Verifique sus credenciales",
    };

    for (const [technical, friendly] of Object.entries(friendlyMessages)) {
      if (errorMessage.includes(technical)) {
        return friendly;
      }
    }

    return "Error inesperado. Por favor, intente nuevamente";
  }

  // ==========================================
  // MÉTODOS LEGACY (COMPATIBILIDAD)
  // ==========================================

  setAuthToken(token) {
    this.token = token;

    if (typeof window !== "undefined") {
      window.authToken = token;
    }

    if (typeof localStorage !== "undefined") {
      localStorage.setItem("auth_token", token);
    }
  }

  getToken() {
    return this.getJWTToken();
  }

  restoreSession() {
    const token = localStorage.getItem("auth_token");
    if (token) {
      this.token = token;
      if (typeof window !== "undefined") {
        window.authToken = token;
      }
      return true;
    }
    return false;
  }
}

// Exportar instancia singleton
const authService = new AuthService();

export default authService;
